/**
 * Collection Search Modal - Full-screen overlay search
 * Integrates with existing collection-search.js logic
 * Shows results in full-screen modal at top of screen
 */

/* START: Modal-specific CSS and JS for full-screen collection search */

(function(window, document) {
  'use strict';

  // ---------------------------------------------------------------------------
  // Query parser & evaluator
  //
  // Supported syntax:
  //   wall maps          AND of words across all fields
  //   -hunting           exclude any product containing "hunting"
  //   "grand canyon"     exact phrase
  //   title:wall         scoped match (also vendor, tag, type/product_type,
  //                       body, handle, variants.title, variants.sku, and
  //                       variants.barcode)
  //   -title:gmu         scoped exclude
  //   pin:product-handle force ONE product to top of results (always shown, even
  //                       if it would not otherwise match)
  //   ^wall              pin a TERM: float every matching product to the top.
  //                       Supports the same scopes/phrases as a normal match
  //                       (^title:wall, ^"grand canyon"). Unlike a clause it
  //                       never filters — it only reorders. Multiple ^terms are
  //                       OR'd, so a product matching any of them floats up.
  // Unknown fields fall through to a literal "field:value" full-text match.
  // ---------------------------------------------------------------------------

  const KNOWN_FIELDS = Object.freeze([
    'title',
    'vendor',
    'tag',
    'type',
    'product_type',
    'body',
    'handle',
    'variants.title',
    'variants.sku',
    'variants.barcode'
  ]);

  const FIELD_LABELS = {
    title: 'title',
    vendor: 'vendor',
    tag: 'tags',
    type: 'product type',
    product_type: 'product type',
    body: 'description',
    handle: 'handle',
    'variants.title': 'variant title',
    'variants.sku': 'variant SKU',
    'variants.barcode': 'variant barcode'
  };

  function queryIssue(message, token, code) {
    const start = token && typeof token.start === 'number' ? token.start : 0;
    const end = token && typeof token.end === 'number' ? token.end : start;
    return { code: code, message: message, index: start, length: Math.max(0, end - start) };
  }

  function tokenizeQuery(input) {
    const tokens = [];
    const errors = [];
    const warnings = [];
    let i = 0;

    function push(type, start, end, extra) {
      tokens.push(Object.assign({ type: type, start: start, end: end }, extra || {}));
    }

    function quotedValue(quoteStart) {
      let value = '';
      let closed = false;
      i++;
      while (i < input.length) {
        if (input[i] === '\\' && i + 1 < input.length) {
          value += input[i + 1];
          i += 2;
          continue;
        }
        if (input[i] === '"') {
          i++;
          closed = true;
          break;
        }
        value += input[i];
        i++;
      }
      if (!closed) {
        errors.push(queryIssue('Missing closing quote.', { start: quoteStart, end: input.length }, 'unclosed_quote'));
      }
      return value;
    }

    while (i < input.length) {
      while (i < input.length && /\s/.test(input[i])) i++;
      if (i >= input.length) break;

      const start = i;
      if (input[i] === '(') {
        push('LPAREN', start, ++i);
        continue;
      }
      if (input[i] === ')') {
        push('RPAREN', start, ++i);
        continue;
      }
      if (input[i] === '-') {
        push('MINUS', start, ++i);
        continue;
      }
      if (input[i] === '^') {
        push('BOOST', start, ++i);
        continue;
      }
      if (input[i] === '&' && (i + 1 === input.length || /[\s()]/.test(input[i + 1]))) {
        push('AND', start, ++i);
        warnings.push(queryIssue('Legacy "&" was treated as AND. Use AND in saved queries.', tokens[tokens.length - 1], 'legacy_ampersand'));
        continue;
      }

      let field = null;
      const fieldMatch = input.slice(i).match(/^([a-zA-Z][a-zA-Z0-9_.-]*):/);
      if (fieldMatch) {
        field = fieldMatch[1].toLowerCase();
        i += fieldMatch[0].length;
      }

      let value = '';
      let phrase = false;
      if (input[i] === '"') {
        phrase = true;
        value = quotedValue(i);
      } else {
        const valueStart = i;
        while (i < input.length && !/[\s()]/.test(input[i])) i++;
        value = input.slice(valueStart, i);
      }

      if (!value) {
        const issueEnd = Math.max(i, start + 1);
        errors.push(queryIssue(field ? 'Expected a value after "' + field + ':".' : 'Expected a search term.', { start: start, end: issueEnd }, 'missing_value'));
        continue;
      }

      const upper = value.toUpperCase();
      if (!field && !phrase && (upper === 'AND' || upper === 'OR' || upper === 'NOT')) {
        push(upper, start, i);
      } else {
        push('TERM', start, i, {
          field: field,
          value: value.toLowerCase(),
          phrase: phrase
        });
      }
    }

    return { tokens: tokens, errors: errors, warnings: warnings };
  }

  function logicalNode(type, left, right) {
    if (!left) return right || null;
    if (!right) return left;
    const children = [];
    if (left.type === type) children.push.apply(children, left.children);
    else children.push(left);
    if (right.type === type) children.push.apply(children, right.children);
    else children.push(right);
    return { type: type, children: children };
  }

  class BooleanQueryParser {
    constructor(tokens, errors) {
      this.tokens = tokens;
      this.errors = errors;
      this.index = 0;
    }

    current() {
      return this.tokens[this.index] || null;
    }

    take(type) {
      const token = this.current();
      if (!token || token.type !== type) return null;
      this.index++;
      return token;
    }

    canStart(token) {
      return !!token && ['TERM', 'LPAREN', 'NOT', 'MINUS', 'BOOST'].indexOf(token.type) !== -1;
    }

    parse() {
      let node = this.parseAnd();
      while (this.current()) {
        const token = this.current();
        if (token.type === 'RPAREN') {
          this.errors.push(queryIssue('Unexpected closing parenthesis.', token, 'unexpected_parenthesis'));
        } else {
          this.errors.push(queryIssue('Unexpected token.', token, 'unexpected_token'));
        }
        this.index++;
        if (this.canStart(this.current())) node = logicalNode('and', node, this.parseAnd());
      }
      return node;
    }

    parseAnd() {
      let left = this.parseOr();
      while (this.current() && this.current().type !== 'RPAREN') {
        const explicit = this.take('AND');
        if (!explicit && !this.canStart(this.current())) break;
        if (!this.canStart(this.current())) {
          this.errors.push(queryIssue('AND must be followed by an expression.', explicit || this.current(), 'missing_and_operand'));
          continue;
        }
        left = logicalNode('and', left, this.parseOr());
      }
      return left;
    }

    parseOr() {
      let left = this.parseUnary();
      while (this.current() && this.current().type === 'OR') {
        const operator = this.take('OR');
        if (!this.canStart(this.current())) {
          this.errors.push(queryIssue('OR must be followed by an expression.', operator, 'missing_or_operand'));
          continue;
        }
        left = logicalNode('or', left, this.parseUnary());
      }
      return left;
    }

    parseUnary() {
      const token = this.current();
      if (!token) return null;
      if (token.type === 'NOT' || token.type === 'MINUS') {
        this.index++;
        if (!this.canStart(this.current())) {
          this.errors.push(queryIssue((token.type === 'NOT' ? 'NOT' : 'Minus') + ' must be followed by an expression.', token, 'missing_not_operand'));
          return null;
        }
        return {
          type: 'not',
          operator: token.type === 'MINUS' ? '-' : 'NOT',
          child: this.parseUnary()
        };
      }
      if (token.type === 'BOOST') {
        this.index++;
        if (!this.canStart(this.current())) {
          this.errors.push(queryIssue('A boost must be followed by a term or grouped expression.', token, 'missing_boost_operand'));
          return null;
        }
        return { type: 'boost', child: this.parseUnary() };
      }
      return this.parsePrimary();
    }

    parsePrimary() {
      const token = this.current();
      if (!token) return null;
      if (token.type === 'LPAREN') {
        this.index++;
        if (this.take('RPAREN')) {
          this.errors.push(queryIssue('Parentheses cannot be empty.', token, 'empty_group'));
          return null;
        }
        const child = this.parseAnd();
        if (!this.take('RPAREN')) {
          this.errors.push(queryIssue('Missing closing parenthesis.', token, 'unclosed_group'));
        }
        return child ? { type: 'group', child: child } : null;
      }
      if (token.type === 'TERM') {
        this.index++;
        if (token.field === 'pin') {
          return { type: 'pin', value: token.value, phrase: token.phrase };
        }
        return {
          type: 'clause',
          field: token.field,
          value: token.value,
          phrase: token.phrase
        };
      }
      this.errors.push(queryIssue('Expected a search term or grouped expression.', token, 'expected_expression'));
      this.index++;
      return null;
    }
  }

  function boostAst(node) {
    if (!node) return null;
    if (node.type === 'clause') return node;
    if (node.type === 'pin') {
      return { type: 'clause', field: 'pin', value: node.value, phrase: node.phrase };
    }
    if (node.type === 'boost') return boostAst(node.child);
    if (node.type === 'not' || node.type === 'group') {
      const child = boostAst(node.child);
      return child ? Object.assign({}, node, { child: child }) : null;
    }
    if (node.type === 'and' || node.type === 'or') {
      const children = node.children.map(boostAst).filter(Boolean);
      if (!children.length) return null;
      if (children.length === 1) return children[0];
      return { type: node.type, children: children };
    }
    return null;
  }

  function extractDirectives(node, state, negated) {
    if (!node) return null;
    if (node.type === 'pin') {
      if (!negated) {
        state.pins.push(node.value);
        return null;
      }
      return { type: 'clause', field: 'pin', value: node.value, phrase: node.phrase };
    }
    if (node.type === 'boost') {
      const target = boostAst(node.child);
      if (target) {
        if (target.type === 'clause') {
          state.boosts.push({ field: target.field, value: target.value, phrase: target.phrase });
        } else {
          state.boosts.push({ ast: target });
        }
      }
      if (negated) {
        state.warnings.push(queryIssue('NOT does not turn a boost into a filter; the boost was kept as an ordering rule.', null, 'negated_boost'));
      }
      return null;
    }
    if (node.type === 'not') {
      const child = extractDirectives(node.child, state, !negated);
      return child ? { type: 'not', operator: node.operator, child: child } : null;
    }
    if (node.type === 'group') {
      const child = extractDirectives(node.child, state, negated);
      return child ? { type: 'group', child: child } : null;
    }
    if (node.type === 'and' || node.type === 'or') {
      const children = node.children.map((child) => extractDirectives(child, state, negated)).filter(Boolean);
      if (!children.length) return null;
      if (children.length === 1) return children[0];
      return { type: node.type, children: children };
    }
    return node;
  }

  function collectClauses(node, negated, clauses) {
    if (!node) return clauses;
    if (node.type === 'clause') {
      clauses.push({
        field: node.field,
        value: node.value,
        negate: !!negated,
        phrase: !!node.phrase
      });
      return clauses;
    }
    if (node.type === 'not') return collectClauses(node.child, !negated, clauses);
    if (node.type === 'group') return collectClauses(node.child, negated, clauses);
    if (node.type === 'and' || node.type === 'or') {
      node.children.forEach((child) => collectClauses(child, negated, clauses));
    }
    return clauses;
  }

  function escapedPhrase(value) {
    return '"' + value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  function serializeClause(node) {
    return (node.field ? node.field + ':' : '') + (node.phrase ? escapedPhrase(node.value) : node.value);
  }

  function astPrecedence(node) {
    if (!node) return 99;
    if (node.type === 'and') return 1;
    if (node.type === 'or') return 2;
    if (node.type === 'not' || node.type === 'boost') return 3;
    return 4;
  }

  function serializeAst(ast, options) {
    const opts = options || {};

    function render(node, parentPrecedence) {
      if (!node) return '';
      if (node.type === 'clause') return serializeClause(node);
      if (node.type === 'pin') {
        return 'pin:' + (node.phrase ? escapedPhrase(node.value) : node.value);
      }
      if (node.type === 'group') return '(' + render(node.child, 0) + ')';
      if (node.type === 'not') {
        const child = render(node.child, astPrecedence(node));
        const formatted = node.operator === '-' ? '-' + child : 'NOT ' + child;
        return astPrecedence(node) < parentPrecedence ? '(' + formatted + ')' : formatted;
      }
      if (node.type === 'boost') {
        const child = render(node.child, astPrecedence(node));
        const formatted = '^' + child;
        return astPrecedence(node) < parentPrecedence ? '(' + formatted + ')' : formatted;
      }
      if (node.type === 'and' || node.type === 'or') {
        const precedence = astPrecedence(node);
        const operator = node.type.toUpperCase();
        let formatted = node.children.map((child) => render(child, precedence)).join(' ' + operator + ' ');
        if (opts.fullyParenthesized || precedence < parentPrecedence) formatted = '(' + formatted + ')';
        return formatted;
      }
      return '';
    }

    return render(ast, 0);
  }

  function describeAst(ast) {
    if (!ast) return '';
    if (ast.type === 'clause') {
      const field = ast.field && KNOWN_FIELDS.indexOf(ast.field) !== -1
        ? FIELD_LABELS[ast.field]
        : 'any searchable field';
      const value = ast.field && KNOWN_FIELDS.indexOf(ast.field) === -1
        ? ast.field + ':' + ast.value
        : ast.value;
      return field + (ast.phrase ? ' has the exact phrase "' : ' contains "') + value + '"';
    }
    if (ast.type === 'pin') return 'pin product "' + ast.value + '" to the top';
    if (ast.type === 'group') return '(' + describeAst(ast.child) + ')';
    if (ast.type === 'not') return 'not (' + describeAst(ast.child) + ')';
    if (ast.type === 'boost') return 'float products matching (' + describeAst(ast.child) + ') to the top';
    if (ast.type === 'and' || ast.type === 'or') {
      return ast.children.map(describeAst).join(ast.type === 'and' ? ' and ' : ' or ');
    }
    return '';
  }

  function formatQuery(parsed, options) {
    if (!parsed) return '';
    if (typeof parsed === 'string') parsed = parseQuery(parsed);
    if (parsed.syntaxAst) return serializeAst(parsed.syntaxAst, options);
    const parts = [];
    const filter = serializeAst(parsed.ast, options);
    if (filter) parts.push(filter);
    (parsed.pins || []).forEach((pin) => parts.push('pin:' + pin));
    (parsed.boosts || []).forEach((boost) => {
      if (boost.ast) parts.push(serializeAst({ type: 'boost', child: boost.ast }, options));
      else parts.push('^' + serializeClause(boost));
    });
    return parts.join(' AND ');
  }

  function normalizeQuery(input, options) {
    return formatQuery(typeof input === 'string' ? parseQuery(input) : input, options);
  }

  function describeQuery(input) {
    const parsed = typeof input === 'string' ? parseQuery(input) : input;
    return parsed ? describeAst(parsed.syntaxAst || parsed.ast) : '';
  }

  function parseQuery(input) {
    const source = typeof input === 'string' ? input : '';
    const tokenized = tokenizeQuery(source);
    tokenized.tokens.forEach((token) => {
      if (token.type === 'TERM' && token.field && token.field !== 'pin' && KNOWN_FIELDS.indexOf(token.field) === -1) {
        tokenized.warnings.push(queryIssue(
          'Unknown field "' + token.field + '" was treated as literal text.',
          token,
          'unknown_field'
        ));
      }
    });
    const parser = new BooleanQueryParser(tokenized.tokens, tokenized.errors);
    const syntaxAst = parser.parse();
    const state = { pins: [], boosts: [], warnings: tokenized.warnings };
    const ast = extractDirectives(syntaxAst, state, false);
    const result = {
      ast: ast,
      syntaxAst: syntaxAst,
      clauses: collectClauses(ast, false, []),
      pins: state.pins,
      boosts: state.boosts,
      errors: tokenized.errors,
      warnings: state.warnings,
      valid: tokenized.errors.length === 0,
      normalized: '',
      plainEnglish: ''
    };
    result.normalized = formatQuery(result);
    result.plainEnglish = describeQuery(result);
    return result;
  }

  function productFieldValue(product, field) {
    if (!product) return '';
    switch (field) {
      case 'title':  return (product.title || '').toLowerCase();
      case 'vendor': return (product.vendor || '').toLowerCase();
      case 'type':
      case 'product_type': return (product.product_type || '').toLowerCase();
      case 'tag':    return (Array.isArray(product.tags) ? product.tags : [product.tags || '']).join(' ').toLowerCase();
      case 'body':   return (product._bodyText || '').toLowerCase();
      case 'handle': return (product.handle || '').toLowerCase();
      case 'variants.title': return productVariantFieldValue(product, 'title');
      case 'variants.sku': return productVariantFieldValue(product, 'sku');
      case 'variants.barcode': return productVariantFieldValue(product, 'barcode');
      default:       return null;
    }
  }

  function productVariantFieldValue(product, field) {
    const variants = product && Array.isArray(product.variants) ? product.variants : [];
    return variants.map((variant) => variant && variant[field] != null ? String(variant[field]) : '').join(' ').toLowerCase();
  }

  function productFullText(product) {
    return [
      product.title || '',
      product.vendor || '',
      product.product_type || '',
      (Array.isArray(product.tags) ? product.tags : [product.tags || '']).join(' '),
      product.handle || '',
      product._bodyText || '',
      productVariantFieldValue(product, 'title'),
      productVariantFieldValue(product, 'sku'),
      productVariantFieldValue(product, 'barcode')
    ].join(' ').toLowerCase();
  }

  function regexEscape(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Phrase clauses (quoted in the query) match on word boundaries:
  //   "wall" matches "wall map" but not "wallaby" or "Cornwall".
  // Bare terms still use substring so partial matches work as users expect
  // while typing.
  function clauseTest(value, isPhrase, haystack) {
    if (isPhrase) {
      const leftBoundary = /^\w/.test(value) ? '\\b' : '';
      const rightBoundary = /\w$/.test(value) ? '\\b' : '';
      const re = new RegExp(leftBoundary + regexEscape(value) + rightBoundary);
      return re.test(haystack);
    }
    return haystack.indexOf(value) !== -1;
  }

  function matchesClause(product, clause, fullText) {
    if (clause.field && KNOWN_FIELDS.indexOf(clause.field) !== -1) {
      const haystack = productFieldValue(product, clause.field);
      const has = clauseTest(clause.value, !!clause.phrase, haystack);
      return clause.negate ? !has : has;
    }
    if (!clause.field) {
      if (fullText == null) fullText = productFullText(product);
      const has = clauseTest(clause.value, !!clause.phrase, fullText);
      return clause.negate ? !has : has;
    }
    // Unknown field (e.g. "foo:bar") — fall through to literal full-text match
    // including the field-name prefix. Phrase semantics don't apply here.
    const needle = clause.field + ':' + clause.value;
    if (fullText == null) fullText = productFullText(product);
    const has = fullText.indexOf(needle) !== -1;
    return clause.negate ? !has : has;
  }

  // A ^term boost matches exactly like a positive (non-negated) clause; it just
  // drives ordering instead of filtering.
  function matchesBoost(product, boost, fullText) {
    if (fullText == null) fullText = productFullText(product);
    if (boost && boost.ast) return evaluateAst(boost.ast, product, fullText);
    return matchesClause(
      product,
      { field: boost.field, value: boost.value, phrase: boost.phrase, negate: false },
      fullText
    );
  }

  function evaluateQuery(parsed, product) {
    if (!parsed) return true;
    if (parsed.valid === false) return false;
    const fullText = productFullText(product);
    if (Object.prototype.hasOwnProperty.call(parsed, 'ast')) {
      return parsed.ast ? evaluateAst(parsed.ast, product, fullText) : true;
    }
    if (!parsed.clauses || !parsed.clauses.length) return true;
    for (let i = 0; i < parsed.clauses.length; i++) {
      if (!matchesClause(product, parsed.clauses[i], fullText)) return false;
    }
    return true;
  }

  function evaluateAst(ast, product, fullText) {
    if (!ast) return true;
    if (fullText == null) fullText = productFullText(product);
    if (ast.type === 'clause') {
      return matchesClause(product, {
        field: ast.field,
        value: ast.value,
        phrase: ast.phrase,
        negate: false
      }, fullText);
    }
    if (ast.type === 'group') return evaluateAst(ast.child, product, fullText);
    if (ast.type === 'not') return !evaluateAst(ast.child, product, fullText);
    if (ast.type === 'and') return ast.children.every((child) => evaluateAst(child, product, fullText));
    if (ast.type === 'or') return ast.children.some((child) => evaluateAst(child, product, fullText));
    return true;
  }

  // Reorder matched products: explicit product pins first, then products that
  // satisfy any ^term boost, then everything else (all in original order).
  // Pins can inject a product that wasn't in the matched set; boosts only
  // reorder products that already matched — they never add non-matching ones.
  function applyPins(parsed, matched, allProducts) {
    matched = Array.isArray(matched) ? matched : [];
    if (!parsed) return matched;
    if (parsed.valid === false) return [];
    const pins = parsed.pins || [];
    const boosts = parsed.boosts || [];
    if (!pins.length && !boosts.length) return matched;

    const handleOf = (p) => (p && p.handle || '').toLowerCase();
    const placed = new Set();

    const pinned = [];
    for (let i = 0; i < pins.length; i++) {
      const handle = pins[i];
      let product = matched.find((p) => handleOf(p) === handle);
      if (!product && allProducts) {
        product = allProducts.find((p) => handleOf(p) === handle);
      }
      if (product && !placed.has(handleOf(product))) {
        pinned.push(product);
        placed.add(handleOf(product));
      }
    }

    const boosted = [];
    if (boosts.length) {
      for (let i = 0; i < matched.length; i++) {
        const product = matched[i];
        const handle = handleOf(product);
        if (placed.has(handle)) continue;
        const fullText = productFullText(product);
        if (boosts.some((b) => matchesBoost(product, b, fullText))) {
          boosted.push(product);
          placed.add(handle);
        }
      }
    }

    const rest = matched.filter((p) => !placed.has(handleOf(p)));
    return pinned.concat(boosted, rest);
  }

  // Cache a stripped body-text field on each product so we don't re-strip HTML
  // on every keystroke.
  function attachBodyText(product) {
    if (product && product._bodyText == null && product.body_html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = product.body_html;
      product._bodyText = (tmp.textContent || tmp.innerText || '');
    }
    return product;
  }

  class CollectionSearchModal {
    constructor(modal, trigger) {
      if (!modal || !trigger) return;

      this.modal = modal;
      this.trigger = trigger;
      this.collectionHandle = this.getCollectionHandle();

      // Config
      this.config = {
        debounceDelay: 300,
        productsPerPage: 250,
        maxDisplayResults: 50,
        minSearchChars: 2,
        descriptionLength: 100
      };

      // DOM elements
      this.overlay = modal.querySelector('[data-modal-overlay]');
      this.container = modal.querySelector('.collection-search-modal-container');
      this.input = modal.querySelector('[data-modal-search-input]');
      this.resultsSection = modal.querySelector('[data-modal-results-section]');
      this.resultsGrid = modal.querySelector('[data-modal-results-grid]');
      this.resultsCount = modal.querySelector('[data-modal-results-count]');
      this.clearBtn = modal.querySelector('[data-modal-clear]');
      this.closeBtn = modal.querySelector('[data-modal-close]');

      // State
      this.allProducts = [];
      this.filteredProducts = [];
      this.isFetching = false;
      this.isFetched = false;
      this.debounceTimer = null;
      this.searchTerm = '';
      this.displayedCount = 0;
      this.isLoadingMore = false;
      this.loadMoreObserver = null;
      this.isOpen = false;

      if (this.input && this.resultsGrid && this.collectionHandle) {
        this.init();
      } else {
        console.warn('CollectionSearchModal: Missing required elements or collection handle');
      }
    }

    init() {
      this.bindEvents();
      this.setupInfiniteScroll();
    }

    setupInfiniteScroll() {
      // Create IntersectionObserver for infinite scroll
      this.loadMoreObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoadingMore) {
            this.loadMoreProducts();
          }
        });
      }, {
        root: this.resultsGrid,
        rootMargin: '100px',
        threshold: 0.1
      });
    }

    bindEvents() {
      // Trigger button - open modal
      this.trigger.addEventListener('click', () => {
        this.openModal();
      });

      // Overlay click - close modal
      if (this.overlay) {
        this.overlay.addEventListener('click', () => {
          this.closeModal();
        });
      }

      // Escape key - close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.closeModal();
        }
      });

      // Input - search as you type
      this.input.addEventListener('input', (e) => {
        this.handleInput(e.target.value);
      });

      // Clear button - clears search text and hides results
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.clearSearch();
          this.input.focus();
        });
      }

      // Prevent form submission
      const form = this.modal.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => e.preventDefault());
      }

      // Handle Add to Cart clicks
      this.resultsGrid.addEventListener('click', (e) => {
        const cartBtn = e.target.closest('.search-product-btn-cart');
        if (cartBtn) {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(cartBtn);
        }
      });

      // Focus trap - prevent tab outside modal
      this.modal.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && this.isOpen) {
          this.trapFocus(e);
        }
      });
    }

    openModal() {
      this.isOpen = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus input
      setTimeout(() => {
        this.input.focus();
      }, 100);

      // Start fetching products in background
      if (!this.isFetched && !this.isFetching) {
        this.fetchAllProducts();
      }
    }

    closeModal() {
      this.isOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.clearSearch();
    }

    trapFocus(e) {
      const focusableElements = this.modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }

    getCollectionHandle() {
      const handle = this.modal.dataset.collectionHandle;
      if (handle) return handle;

      const match = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
      return match ? match[1] : null;
    }

    handleInput(value) {
      const term = value.trim().toLowerCase();
      this.searchTerm = term;

      clearTimeout(this.debounceTimer);

      if (!term) {
        this.hideResultsSection();
        this.filteredProducts = [];
        return;
      }

      if (term.length < this.config.minSearchChars) {
        this.hideResultsSection();
        return;
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch(term);
      }, this.config.debounceDelay);
    }

    async performSearch(term) {
      if (!this.isFetched) {
        if (!this.isFetching) {
          await this.fetchAllProducts();
        } else {
          await this.waitForFetch();
        }
      }

      this.filteredProducts = this.filterProducts(term);
      this.renderResults(term);
    }

    waitForFetch() {
      return new Promise((resolve) => {
        const checkFetch = setInterval(() => {
          if (this.isFetched) {
            clearInterval(checkFetch);
            resolve();
          }
        }, 100);
      });
    }

    async fetchAllProducts() {
      if (this.isFetching || this.isFetched) return;

      this.isFetching = true;

      try {
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const url = `/collections/${this.collectionHandle}/products.json?limit=${this.config.productsPerPage}&page=${page}`;
          const response = await fetch(url);

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const data = await response.json();

          if (data.products && data.products.length > 0) {
            this.allProducts = this.allProducts.concat(data.products);
            page++;

            // Update results while fetching
            if (this.searchTerm) {
              this.filteredProducts = this.filterProducts(this.searchTerm);
              this.renderResults(this.searchTerm, true);
            }

            if (data.products.length < this.config.productsPerPage) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        this.isFetched = true;
        console.log(`CollectionSearchModal: Loaded ${this.allProducts.length} products`);

        if (this.searchTerm) {
          this.filteredProducts = this.filterProducts(this.searchTerm);
          this.renderResults(this.searchTerm);
        }

      } catch (error) {
        console.error('CollectionSearchModal: Fetch error', error);
        this.showMessage('Error loading products. Please try again.');
      } finally {
        this.isFetching = false;
      }
    }

    filterProducts(term) {
      const parsed = parseQuery(term);
      this.lastParsedQuery = parsed;

      const matched = this.allProducts.filter((product) => {
        attachBodyText(product);
        return evaluateQuery(parsed, product);
      });

      return applyPins(parsed, matched, this.allProducts);
    }

    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    }

    renderResults(term, isPartial = false) {
      if (!this.resultsGrid) return;

      const totalFound = this.filteredProducts.length;

      // Check if this is truly a NEW search (different term) vs just updating counts
      const currentCards = this.resultsGrid.querySelectorAll('.collection-search-product-card');
      const shouldClearGrid = currentCards.length === 0 || !isPartial;

      if (shouldClearGrid) {
        // Reset displayed count for new search
        this.displayedCount = 0;
        this.resultsGrid.innerHTML = '';

        // Disconnect existing observer
        if (this.loadMoreObserver) {
          this.loadMoreObserver.disconnect();
        }
      }

      // Show results section
      this.showResultsSection();

      // Update count
      if (this.resultsCount) {
        if (isPartial && this.isFetching) {
          this.resultsCount.innerHTML = `<span class="loading-text">(${totalFound} found, loading...)</span>`;
        } else if (totalFound === 0) {
          this.resultsCount.textContent = '';
        } else {
          this.resultsCount.textContent = `(${totalFound})`;
        }
      }

      if (totalFound === 0 && !this.isFetching) {
        this.resultsGrid.innerHTML = `
          <div class="collection-search-no-results">
            <p>No products match "${this.escapeHtml(term)}"</p>
            <p class="hint">If you don't see what you're looking for, please click below</p>
            <a href="/collections/all" class="btn btn-border">Browse All Products</a>
          </div>
        `;
        return;
      }

      // Only append products if we cleared the grid (new search)
      if (shouldClearGrid) {
        this.appendProducts(term, this.config.maxDisplayResults);
      }
    }

    appendProducts(term, count) {
      const startIndex = this.displayedCount;
      const endIndex = Math.min(startIndex + count, this.filteredProducts.length);
      const productsToAdd = this.filteredProducts.slice(startIndex, endIndex);

      // Remove existing load more element if present
      const existingLoadMore = this.resultsGrid.querySelector('.collection-search-load-more');
      if (existingLoadMore) {
        existingLoadMore.remove();
      }

      productsToAdd.forEach(product => {
        const card = this.createProductCard(product, term);
        this.resultsGrid.appendChild(card);
      });

      this.displayedCount = endIndex;

      // Update count display
      if (this.resultsCount) {
        const totalFound = this.filteredProducts.length;
        if (this.isFetching) {
          this.resultsCount.innerHTML = `<span class="loading-text">(${totalFound} found, loading...)</span>`;
        } else {
          this.resultsCount.textContent = `(${totalFound})`;
        }
      }

      // Add load more trigger if there are more products
      if (this.displayedCount < this.filteredProducts.length) {
        this.addLoadMoreTrigger();
      }
    }

    addLoadMoreTrigger() {
      const loadMore = document.createElement('div');
      loadMore.className = 'collection-search-load-more';
      loadMore.innerHTML = `
        <svg class="collection-search-load-more-spinner" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" opacity="0.25"/>
          <path d="M10 2a8 8 0 0 1 8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <span>Loading more products...</span>
      `;
      this.resultsGrid.appendChild(loadMore);

      // Observe the load more element
      if (this.loadMoreObserver) {
        this.loadMoreObserver.observe(loadMore);
      }
    }

    loadMoreProducts() {
      if (this.isLoadingMore || this.displayedCount >= this.filteredProducts.length) return;

      this.isLoadingMore = true;

      // Small delay for smooth UX
      setTimeout(() => {
        this.appendProducts(this.searchTerm, this.config.maxDisplayResults);
        this.isLoadingMore = false;
      }, 300);
    }

    createProductCard(product, searchTerm) {
      const card = document.createElement('div');
      card.className = 'collection-search-product-card';

      // Image
      const imageUrl = product.images && product.images[0]
        ? product.images[0].src.replace(/\.([^\.]+)$/, '_300x.$1')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';

      // Price
      const variant = product.variants && product.variants[0];
      const price = variant ? this.formatMoney(variant.price) : '';
      const comparePrice = variant && variant.compare_at_price
        ? this.formatMoney(variant.compare_at_price)
        : '';

      // Description - truncate to config length
      const description = product.body_html
        ? this.truncateText(this.stripHtml(product.body_html), this.config.descriptionLength)
        : '';

      // Highlight matching text in title
      const highlightedTitle = this.highlightMatches(product.title, searchTerm);

      // Variant ID for add to cart
      const variantId = variant ? variant.id : '';

      card.innerHTML = `
        <a href="/products/${product.handle}" class="search-product-image">
          <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" onload="this.classList.add('loaded')">
        </a>
        <div class="search-product-content">
          <div class="search-product-price">
            ${comparePrice ? `<span class="compare-price">${comparePrice}</span>` : ''}
            <span>${price}</span>
          </div>
          <div class="search-product-info">
            <h4 class="search-product-title">
              <a href="/products/${product.handle}">${highlightedTitle}</a>
            </h4>
            ${description ? `<p class="search-product-description">${this.escapeHtml(description)}</p>` : ''}
          </div>
          <div class="search-product-footer">
            <div class="search-product-buttons">
              <a href="/products/${product.handle}" class="search-product-btn search-product-btn-view">View Product</a>
              <button type="button"
                      class="search-product-btn search-product-btn-cart"
                      data-variant-id="${variantId}"
                      ${!variant || !variant.available ? 'disabled' : ''}>
                ${variant && variant.available ? 'Add To Cart' : 'Sold Out'}
              </button>
            </div>
          </div>
        </div>
      `;

      // Handle cached images that load instantly
      const img = card.querySelector('img');
      if (img && img.complete) {
        img.classList.add('loaded');
      }

      return card;
    }

    truncateText(text, maxLength) {
      if (!text) return '';
      text = text.trim();
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + '...';
    }

    highlightMatches(text, term) {
      if (!term) return this.escapeHtml(text);

      const result = this.escapeHtml(text);
      let parsed = this.lastParsedQuery;
      if (!parsed) {
        try {
          parsed = parseQuery(term);
        } catch (error) {
          return result;
        }
      }
      if (!parsed || parsed.valid === false) return result;

      // Highlight only positive terms that can match the product title. Parsing
      // first prevents Boolean operators and parentheses from being emphasized
      // as if they were search words.
      const clauses = Array.isArray(parsed.clauses) ? parsed.clauses : [];
      const needles = clauses
        .filter((clause) => clause && !clause.negate && (!clause.field || clause.field === 'title') && clause.value)
        .map((clause) => this.escapeHtml(String(clause.value)))
        .filter(Boolean);
      const uniqueNeedles = Array.from(new Set(needles)).sort((a, b) => b.length - a.length);
      if (!uniqueNeedles.length) return result;

      const regex = new RegExp('(' + uniqueNeedles.map((needle) => this.escapeRegex(needle)).join('|') + ')', 'gi');
      return result.replace(regex, '<mark>$1</mark>');
    }

    formatMoney(price) {
      // Shopify API returns price as string in dollars (e.g., "15.00")
      const amount = parseFloat(price).toFixed(2);
      return `$${amount}`;
    }

    escapeHtml(text) {
      if (text == null) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalText = button.textContent;
      button.textContent = 'Adding...';
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        if (response.ok) {
          button.textContent = 'Added!';
          button.style.background = '#2a6';
          button.style.borderColor = '#2a6';

          // Update cart count if there's a cart counter element
          this.updateCartCount();

          setTimeout(() => {
            button.textContent = originalText;
            button.style.background = '';
            button.style.borderColor = '';
            button.disabled = false;
          }, 2000);
        } else {
          throw new Error('Add to cart failed');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    }

    updateCartCount() {
      // Try to update common cart count selectors
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const countElements = document.querySelectorAll('.cart-count, .cart-quantity, [data-cart-count], .tt-cart-box .tt-badge-cart');
          countElements.forEach(el => {
            el.textContent = cart.item_count;
          });
        })
        .catch(() => {});
    }

    showResultsSection() {
      if (this.resultsSection) {
        this.resultsSection.style.display = 'flex';
      }
    }

    hideResultsSection() {
      if (this.resultsSection) {
        this.resultsSection.style.display = 'none';
      }
      if (this.resultsGrid) {
        this.resultsGrid.innerHTML = '';
      }
      if (this.resultsCount) {
        this.resultsCount.textContent = '';
      }
    }

    // Loader removed - no loading indicator needed

    clearSearch() {
      this.input.value = '';
      this.searchTerm = '';
      this.filteredProducts = [];
      this.displayedCount = 0;

      // Disconnect observer
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
      }

      this.hideResultsSection();
    }
  }

  // Auto-initialize
  function init() {
    const modal = document.querySelector('[data-collection-search-modal]');
    const trigger = document.querySelector('[data-collection-search-trigger]');

    if (modal && trigger && !modal.dataset.initialized) {
      const instance = new CollectionSearchModal(modal, trigger);
      modal.dataset.initialized = 'true';

      // Bind search suggestion buttons
      bindSearchSuggestions(instance);
    }
  }

  // Bind search suggestion buttons to open modal and search.
  // Buttons may carry a data-query attribute with the full boolean expression;
  // if absent we fall back to the button text (backwards compat).
  function bindSearchSuggestions(modalInstance) {
    const suggestionButtons = document.querySelectorAll('.search-suggestion');

    suggestionButtons.forEach(button => {
      button.addEventListener('click', function() {
        const query = (this.dataset.query || this.textContent || '').trim();
        if (!query) return;

        modalInstance.openModal();

        if (modalInstance.input) {
          modalInstance.input.value = query;
          modalInstance.handleInput(query);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CollectionSearchModal = CollectionSearchModal;

  // Exposed for the query builder dashboard so it can run the same logic
  // against any collection without instantiating a modal.
  window.CollectionSearchQuery = {
    parseQuery: parseQuery,
    evaluateQuery: evaluateQuery,
    evaluateAst: evaluateAst,
    applyPins: applyPins,
    attachBodyText: attachBodyText,
    productFullText: productFullText,
    productFieldValue: productFieldValue,
    matchesClause: matchesClause,
    matchesBoost: matchesBoost,
    serializeAst: serializeAst,
    formatQuery: formatQuery,
    normalizeQuery: normalizeQuery,
    describeQuery: describeQuery,
    KNOWN_FIELDS: KNOWN_FIELDS
  };

})(window, document);

/* END: Modal-specific CSS and JS for full-screen collection search */
